import type { ComponentType } from 'react';
import { BadgeCheck, CircleDashed, CircleSlash, Hand } from 'lucide-react';
import { DEPARTMENT_META, TICKET_STATUS_META, type Department, type TicketStatus } from '@ssn/shared';
import { Badge, type Tone } from '@/components/ui/Badge';
import { cn } from '@/components/ui/cn';

type IconComponent = ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;

const STATUS_ICONS: Record<TicketStatus, IconComponent> = {
  NEW: CircleDashed,
  CLAIMED: Hand,
  COMPLETED: BadgeCheck,
  DENIED: CircleSlash,
};

export function TicketStatusBadge({
  status,
  size = 'md',
  className,
}: {
  status: TicketStatus;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const meta = TICKET_STATUS_META[status];
  const Icon = STATUS_ICONS[status];
  return (
    <Badge
      tone={meta.tone as Tone}
      size={size}
      title={meta.description}
      icon={<Icon aria-hidden className={size === 'sm' ? 'size-3' : 'size-3.5'} />}
      className={className}
    >
      {meta.label}
    </Badge>
  );
}

/**
 * The department accent (§ department colours) — always paired with the
 * three-letter department code as text, never colour alone (§14), and always
 * a distinct tone from any status badge it might sit beside.
 */
export function DepartmentBadge({
  department,
  size = 'md',
  className,
}: {
  department: Department;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const meta = DEPARTMENT_META[department];
  return (
    <Badge tone={meta.tone as Tone} size={size} className={cn('font-semibold', className)}>
      {meta.label}
    </Badge>
  );
}
